import { theme as fdsTheme } from '@fountain/fountain-ui-components';

// Ripple emits its tokens through Tailwind v4's `prefix(ds)`, so the custom
// property is `--ds-color-neutral-*` rather than `--color-neutral-*`. The
// stylesheet is loaded once in `app.js`; the hex fallback covers any context
// that renders without it.
const RIPPLE_COLORS: Record<string, [token: string, hex: string]> = {
  '#131e40': ['neutral-900', '#0f172a'],
  '#141414': ['neutral-900', '#0f172a'],
  '#344063': ['neutral-600', '#404d5f'],
  '#666f89': ['neutral-400', '#687281'],
  '#969eb3': ['neutral-400', '#687281'],
  '#b4b9cc': ['neutral-300', '#b7c0cb'],
  '#d6d9e4': ['neutral-200', '#e2e8f0'],
  '#e9ebf2': ['neutral-200', '#e2e8f0'],
  '#f2f3f7': ['neutral-100', '#f1f5f9'],
  '#fbfbfa': ['neutral-50', '#f8fafc'],
  '#ffdde7': ['rose-100', '#ffe4e6'],
  '#e51f58': ['danger', '#e11d48'],
  '#c20d42': ['danger-hover', '#9f1239'],
  '#320d18': ['rose-800', '#9f1239'],
  '#dff8ec': ['green-50', '#d8f9d3'],
  '#29c278': ['success', '#3e8334'],
  '#009e52': ['success', '#3e8334'],
  '#134030': ['green-800', '#145b2f'],
  '#fff3dd': ['amber-50', '#fff8e1'],
  '#ffda99': ['amber-100', '#fef3c7'],
  '#ff721a': ['warning', '#f68006'],
  '#cc8200': ['warning', '#f68006'],
  '#403013': ['amber-800', '#92400e'],
  // Brand blue binds by hex because the informational roles below hold their
  // own tokens. `tokenFor` lets a role win over the hex, so `infoMark` keeps
  // `blue-600` and `infoTint` keeps `blue-50` while every other reader of the
  // bridged theme takes the brand.
  //
  // FDS spends two rungs where Ripple spends one, so `blue50` and `blue100`
  // both land on `brand-50`. That is what carries every call site holding a
  // raw `common.blue100` outside a role.
  '#edf6ff': ['brand-50', '#eef2ff'],
  '#e0efff': ['brand-50', '#eef2ff'],
  '#1260fd': ['primary', '#4f46e5'],
  '#006eda': ['brand-800', '#3730a3'],
  '#0057ad': ['brand-800', '#3730a3'],
};

// Roles whose Ripple target is decided by what the role is FOR, not by the FDS
// value it happens to hold. Without these the ramp collapses two roles onto one
// token and the distinction the role split exists to protect disappears —
// `success` and `successHover` both hold greens that map to `success`, so the
// active-call button would rest and hover on the same colour.
//
// `danger` and `dangerHover` deliberately share `danger-hover`: both hold
// red600, and no call site pairs them as a rest and a hover, so there is no
// distinction to protect.
const ROLE_TOKENS: Record<string, readonly [token: string, hex: string]> = {
  // Blue is the one ramp where the hex map cannot decide alone. `blue100` and
  // `blue400` are read by the brand as often as by information — a primary
  // button and an info glyph hold the same FDS value — so the hex entries
  // carry the brand and these roles hold the informational half back.
  //
  // `infoMark` is 3.86:1 on white: it clears SC 1.4.11's 3:1 non-text minimum
  // and fails AA's 4.5:1, so it paints stripes and glyphs and never text.
  informational: ['blue-50', '#e7f3ff'],
  infoMark: ['blue-600', '#357fef'],
  infoTint: ['blue-50', '#e7f3ff'],
  successHover: ['success-hover', '#145b2f'],
  warningText: ['amber-800', '#92400e'],
  // orange200 as a fill becomes a tint, but a border on that tint has to stay
  // visible against it, so this role takes the saturated amber instead.
  warningBorder: ['warning', '#f68006'],
  // The brand tint is one rung. The rung above it is spent only on a hover
  // that sits on that tint, which is why `brandTint` carries a selected row
  // with no hover and a hover with no tint under it alike.
  brandIconTint: ['brand-50', '#eef2ff'],
  brandTint: ['brand-50', '#eef2ff'],
  brandSelectedHover: ['brand-100', '#e0e7ff'],
  brandTintHover: ['brand-100', '#e0e7ff'],
  // A border on a tint that has to stay visible against it, the same call
  // `warningBorder` takes. 1.43:1 becomes 5.62:1.
  brandTintBorder: ['primary', '#4f46e5'],
  // A border that only closes a region reads the rung above the tint instead,
  // at 1.10:1. FDS spent two rungs here, so without its own name this edge
  // lands on the tint it sits on and disappears.
  brandTintEdge: ['brand-100', '#e0e7ff'],
};

export const cssVar = (token: string, hex: string) =>
  `var(--ds-color-${token}, ${hex})`;

// Which green a call site takes is decided by what it paints, not by the FDS
// value it holds. A fill, a border or an SVG glyph takes `green-400`: it has to
// stay visible against the grey counterpart it swaps with, and on `success` it
// sits at 1.04:1 against that grey. Text takes `success`, because `green-400`
// is 3.27:1 on white and text owes 4.5:1.
//
// One shape the rule does not cover: a fill whose counterpart is the light
// `neutral-200` track it sits in rather than a `neutral-400` sibling. There
// `success` is 3.79:1 and `green-400` is 2.66:1, so a progress bar keeps
// `success` — a bar's fill against its own track is what carries the value.
//
// No FDS value maps onto `green-400`, so it is not a `RIPPLE_COLORS` target. It
// is one exported binding rather than a literal retyped at each mark, which is
// also what puts it in `rippleColors` for the declaration test to check.
const GREEN_MARK: readonly [token: string, hex: string] = [
  'green-400',
  '#3ea22f',
];
export const greenMark = cssVar(...GREEN_MARK);

// The Internal sourcing glyph. `purple-600` is 4.5:1 on white, above the 3:1 non-text minimum,
// and no FDS value maps onto it, so it binds here like `green-400`.
const PURPLE_MARK: readonly [token: string, hex: string] = [
  'purple-600',
  '#a738ff',
];
export const purpleMark = cssVar(...PURPLE_MARK);

// Ripple's blue ramp is three rungs. A stacked chart series has to stay legible
// on the days the other segment has no value, which rules out `blue-50` at
// 1.13:1 on white; `blue-800` over `blue-600` is the one pair where each rung
// clears the 3:1 non-text minimum standing alone. The two separate from each
// other at 2.37:1, which is what a three-rung ramp costs. No FDS value maps
// onto `blue-800`, so it binds here to reach `rippleColors` and the
// declaration test.
const BLUE_MARK_DARK: readonly [token: string, hex: string] = [
  'blue-800',
  '#0c4497',
];

// Canvas, chart libraries and `<img>`-loaded SVG parse their value outside the
// CSS cascade, where `var()` never resolves. Those call sites read the raw hex
// here so the ramp still has one source. An inline SVG is not one of them: its
// presentation attributes are parsed as CSS property values, so `var()` does
// resolve there.
export const rippleColors = Object.fromEntries(
  [
    ...Object.values(RIPPLE_COLORS),
    ...Object.values(ROLE_TOKENS),
    GREEN_MARK,
    PURPLE_MARK,
    BLUE_MARK_DARK,
  ].map(([token, hex]) => [token, hex]),
) as Record<string, string>;

export const rippleNeutral = Object.fromEntries(
  Object.entries(rippleColors).filter(([token]) =>
    token.startsWith('neutral-'),
  ),
) as Record<string, string>;

// FDS writes gray800 as an rgb triple in two theme values, which the hex map
// cannot match. `color-mix` carries the alpha while keeping the color itself
// on the variable; a hex-valued variable cannot sit inside `rgba()`. Keyed on
// the triple so the black shadows and the blue focus outline are left alone.
const FDS_GRAY800_RGB =
  /rgba?\(\s*19\s*,\s*30\s*,\s*64\s*(?:,\s*([\d.]+)\s*)?\)/gi;

const [GRAY800_TOKEN, GRAY800_HEX] = RIPPLE_COLORS['#131e40'];
const SECONDARY_TEXT_HEX = rippleNeutral['neutral-600'];

const HEX_ONLY = Object.entries(RIPPLE_COLORS).map(
  ([fdsColor, [, hex]]) => [fdsColor, hex] as const,
);

// Material-UI parses these palette values in JavaScript while it builds
// component styles — `fade`, `darken` and `lighten` all reject `var()` and
// throw. Everything under `palette` keeps a literal hex for that reason,
// except `common` and `alertTypesBackgroundColors`, which Material-UI does
// not augment.
//
// Exempt is not the same as safe: a call site can hand a `common` value to
// `lighten` itself, and that throws just the same. Those call sites read the
// raw hex out of `rippleColors` instead, the way a Recharts `fill` does in
// `Sourcing/components/SourcingDetailChart/components/applicants-bar-chart.js`.
const isMuiParsed = (path: string[]) =>
  path[0] === 'palette' &&
  path.length > 1 &&
  path[1] !== 'common' &&
  path[1] !== 'alertTypesBackgroundColors';

const BARE_HEX = /^#[0-9a-f]{3,8}$/i;

const tokenFor = (fdsColor: string, path: string[]) => {
  const role = path.at(-1);
  return (role && ROLE_TOKENS[role]) || RIPPLE_COLORS[fdsColor];
};

const replaceFdsGrays = (value: unknown, path: string[] = []): unknown => {
  if (Array.isArray(value)) {
    return value.map(item => replaceFdsGrays(item, path));
  }

  if (value && typeof value === 'object') {
    return Object.fromEntries(
      Object.entries(value).map(([key, nestedValue]) => [
        key,
        replaceFdsGrays(nestedValue, [...path, key]),
      ]),
    );
  }

  if (typeof value === 'string') {
    if (isMuiParsed(path)) {
      return HEX_ONLY.reduce(
        (result, [fdsColor, hex]) =>
          result.replace(new RegExp(fdsColor, 'gi'), hex),
        value,
      ).replace(FDS_GRAY800_RGB, (_, alpha?: string) =>
        alpha === undefined ? GRAY800_HEX : `rgba(15, 23, 42, ${alpha})`,
      );
    }

    // A role whose FDS value has no `RIPPLE_COLORS` entry still has to bind.
    // `brandTintBorder` and `brandTintHover` are the live cases: their FDS
    // values carry no hex entry, so without this the loop below leaves them on
    // FDS. A role holding nothing but a hex is the whole value, so it binds
    // directly.
    const roleToken = ROLE_TOKENS[path.at(-1) ?? ''];
    if (roleToken && BARE_HEX.test(value)) {
      return cssVar(...roleToken);
    }

    const hexMapped = Object.keys(RIPPLE_COLORS).reduce((result, fdsColor) => {
      const [token, hex] = tokenFor(fdsColor, path);
      return result.replace(new RegExp(fdsColor, 'gi'), cssVar(token, hex));
    }, value);

    return hexMapped.replace(FDS_GRAY800_RGB, (_, alpha?: string) => {
      const color = cssVar(GRAY800_TOKEN, GRAY800_HEX);
      if (alpha === undefined) {
        return color;
      }
      return `color-mix(in srgb, ${color} ${Number(alpha) * 100}%, transparent)`;
    });
  }

  return value;
};

// The clone keeps every key and passes functions through by reference, so the
// shape matches; the traversal cannot prove that to the compiler.
const mappedTheme = replaceFdsGrays(fdsTheme) as typeof fdsTheme;

// Only the semantic text path moves. `gray600` carries both text and
// fills, so remapping the ramp would drag borders and icons with it —
// but that leaves the `common.gray600` text call sites on the old value,
// and a screen can show both grays. They wait on the accessibility sweep.
// Material-UI v4's ButtonBase pulses its focus ripple from a passive effect
// that dereferences the TouchRipple ref without a guard. Under React 18 that
// effect can run in the same commit that mounts the button, before
// TouchRipple exists — a button that mounts already focus-visible (a
// dialog's autofocused action, opened from the keyboard) throws on
// `rippleRef.current.pulsate()`. These are every component recruiter-ui
// renders that turns `focusRipple` on; the rest leave it off. Click ripples
// and the focus-visible styling are unaffected.
const FOCUS_RIPPLE_OFF = { disableFocusRipple: true } as const;

const theme = {
  ...mappedTheme,
  palette: {
    ...mappedTheme.palette,
    text: {
      ...mappedTheme.palette?.text,
      secondary: SECONDARY_TEXT_HEX,
    },
  },
  props: {
    ...mappedTheme.props,
    MuiButton: { ...mappedTheme.props?.MuiButton, ...FOCUS_RIPPLE_OFF },
    MuiFab: { ...mappedTheme.props?.MuiFab, ...FOCUS_RIPPLE_OFF },
    MuiIconButton: { ...mappedTheme.props?.MuiIconButton, ...FOCUS_RIPPLE_OFF },
    MuiTab: { ...mappedTheme.props?.MuiTab, ...FOCUS_RIPPLE_OFF },
    MuiToggleButton: FOCUS_RIPPLE_OFF,
  },
};

export default theme;
